/**
 * lowFiMode — automatic minimum-spec rendering profile.
 *
 * On software GL stacks (SwiftShader in headless Chromium and CI
 * containers, Mesa llvmpipe on GPU-less Linux boxes) the full visual
 * stack is unplayable for reasons that profiling pinned precisely:
 *
 *   1. Uniform-buffer exhaustion: only ~14 vertex UBO bindings exist,
 *      every PBR material fails to compile, and Babylon retries each
 *      failing effect every frame (~30 compile errors/second measured).
 *      Handled by `Engine.ts` via `shouldDisableUniformBuffers()`.
 *   2. Shader-variant churn: ranged point lights attached to moving
 *      NPCs (fox eyes, elder beacons) drift in and out of world-mesh
 *      light lists, flipping material defines and forcing a shader
 *      re-link nearly every frame. SwiftShader links cost ~1s each —
 *      the game pins at ~1 fps while `scene.render()` itself is cheap.
 *   3. Full-screen pass stacking: bloom + chromatic aberration + grain
 *      + vignette + FXAA + glow-layer + shadow maps multiply the raster
 *      cost of an already CPU-bound renderer.
 *
 * Low-fi mode keeps gameplay byte-identical (physics, puzzle logic,
 * prompts, NPC behavior are untouched) and only strips render-side
 * cost: hemispheric + directional lighting only, frozen tiny shadow
 * map, no post-processing, no glow, reduced internal resolution.
 *
 * Activation: automatic on detected software renderers, overridable
 * either way with `?lofi=1` / `?lofi=0` — so the QA harness, players on
 * weak hardware, and debugging sessions can all force the choice.
 */

let cachedSoftwareRenderer: boolean | null = null;

/** True when WebGL is served by a software rasterizer (SwiftShader/llvmpipe). */
export function isSoftwareRenderer(): boolean {
  if (cachedSoftwareRenderer !== null) return cachedSoftwareRenderer;
  cachedSoftwareRenderer = detectSoftwareRenderer();
  return cachedSoftwareRenderer;
}

function detectSoftwareRenderer(): boolean {
  try {
    if (typeof document === "undefined") return false;
    const probe = document.createElement("canvas");
    const gl =
      probe.getContext("webgl2") ??
      (probe.getContext("webgl") as WebGLRenderingContext | null);
    if (!gl) return false;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = dbg
      ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
      : String(gl.getParameter(gl.RENDERER));
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return /swiftshader|llvmpipe|softpipe|software/i.test(renderer);
  } catch {
    return false;
  }
}

/** Query-param override: `?lofi=1` forces on, `?lofi=0` forces off. */
function lowFiOverride(): boolean | null {
  try {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("lofi");
    if (value === "1") return true;
    if (value === "0") return false;
    return null;
  } catch {
    return null;
  }
}

/** Whether the minimum-spec rendering profile is active. */
export function isLowFiMode(): boolean {
  return lowFiOverride() ?? isSoftwareRenderer();
}

/** Test hook: reset the cached renderer detection. */
export function resetLowFiDetectionForTests(): void {
  cachedSoftwareRenderer = null;
}
